import React, { createContext, useContext, useState, useEffect } from 'react';
import { useWallet } from './WalletContext';
import { useContracts } from './ContractContext';
import { toast } from 'react-toastify';

// Create a context for easier access to contract services and additional helper functions
const ContractServicesContext = createContext();

/**
 * Provider component that makes contract services available throughout the app
 * This acts as a wrapper around ContractContext with additional utilities
 */
export function ContractServicesProvider({ children }) {
  const { services, loading: contractsLoading, error: contractsError } = useContracts();
  const { isConnected, account, chainId, isCorrectNetwork } = useWallet();
  const [transactionInProgress, setTransactionInProgress] = useState(false);

  // Helper function to handle contract transactions with standardized error handling and feedback
  const executeContractTransaction = async (
    contractFunction,
    args = [],
    options = { 
      successMessage: 'Transaction successful!', 
      failMessage: 'Transaction failed', 
      pendingMessage: 'Transaction in progress...',
      showToast: true
    }
  ) => {
    if (!isConnected) {
      const msg = 'Please connect your wallet to perform this action';
      if (options.showToast) toast.error(msg);
      throw new Error(msg);
    }

    if (!isCorrectNetwork) {
      const msg = 'Please switch to the correct network to perform this action';
      if (options.showToast) toast.error(msg);
      throw new Error(msg);
    }

    try {
      setTransactionInProgress(true);
      if (options.showToast) toast.info(options.pendingMessage);
      
      // Execute the contract function
      const result = await contractFunction(...args);
      
      if (options.showToast) toast.success(options.successMessage);
      return result;
    } catch (error) {
      console.error('Contract transaction error:', error);
      const errorMessage = error.message || options.failMessage;
      if (options.showToast) toast.error(errorMessage);
      throw error;
    } finally {
      setTransactionInProgress(false);
    }
  };

  const value = {
    services,
    loading: contractsLoading,
    error: contractsError,
    isReady: !contractsLoading && !!services ,
    executeContractTransaction,
    transactionInProgress,
  };

  return (
    <ContractServicesContext.Provider value={value}>
      {children}
    </ContractServicesContext.Provider>
  );
}

/**
 * Hook for accessing contract services
 */
export function useContractServices() {
  const context = useContext(ContractServicesContext);
  if (context === undefined) {
    throw new Error('useContractServices must be used within a ContractServicesProvider');
  }
  return context;
}

export default ContractServicesContext; 